/**
 * Copyright IBM Corp. 2021
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from "react";
import hoistStatics from "hoist-non-react-statics";
import PropTypes from "prop-types";
import { TriDictionaryContext } from "./TriDictionaryProvider";

export function withTriDictionary(WrappedComponent) {
  const displayName = `withTriDictionary(${
    WrappedComponent.displayName || WrappedComponent.name
  })`;
  const WithTriDictionary = React.memo(({ forwardedRef, ...rest }) => {
    const appMessages = React.useContext(TriDictionaryContext);
    if (appMessages == null) {
      throw new Error(
        `Could not find "appMessages" in the context of " ${displayName}. Wrap the application root component in a <TriDictionaryProvider>.`
      );
    }
    return (
      <WrappedComponent
        ref={forwardedRef}
        {...rest}
        appMessages={appMessages}
      />
    );
  });
  WithTriDictionary.displayName = displayName;
  WithTriDictionary.WrappedComponent = WrappedComponent;
  WithTriDictionary.propTypes = {
    forwardedRef: PropTypes.any,
  };
  hoistStatics(WithTriDictionary, WrappedComponent);
  return React.forwardRef((props, ref) => {
    return <WithTriDictionary {...props} forwardedRef={ref} />;
  });
}
